import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";

const SESSION_KEY = "lac_combo_popup_seen";

/**
 * ComboPopup — slides in once when the user scrolls the Services section into view.
 * Suppressed for the rest of the session after first view OR after dismissal.
 */
export default function ComboPopup({ targetSelector = "#services" }) {
  const [open, setOpen] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(SESSION_KEY) === "1") return;

    const target = document.querySelector(targetSelector);
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && entry.intersectionRatio >= 0.25 && !firedRef.current) {
          firedRef.current = true;
          // Small delay so the popup feels intentional, not jumpy
          setTimeout(() => setOpen(true), 600);
        }
      },
      { threshold: [0, 0.25, 0.5, 1] },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [targetSelector]);

  const close = () => {
    setOpen(false);
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) { void e; }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={close}
        className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm animate-[fadeIn_.25s_ease-out]"
      />

      {/* Card */}
      <div
        role="dialog"
        aria-labelledby="combo-popup-title"
        data-testid="combo-popup"
        className="fixed inset-0 z-[81] flex items-end sm:items-center justify-center p-4 pointer-events-none"
      >
        <div className="pointer-events-auto relative w-full max-w-lg overflow-hidden rounded-sm border border-[#FF2D87]/40 bg-gradient-to-br from-[#D946EF]/15 via-[#0a0a0a] to-[#FF2D87]/15 p-8 shadow-[0_0_80px_rgba(255,45,135,0.25)] animate-[slideUp_.35s_cubic-bezier(.2,.8,.2,1)]">
          {/* Glow lines */}
          <div className="pointer-events-none absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FF2D87] to-transparent opacity-70" />

          <button
            type="button"
            data-testid="combo-popup-close"
            onClick={close}
            aria-label="Dismiss combo offer"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 border border-white/10 hover:border-white/30 flex items-center justify-center text-zinc-300 hover:text-white transition"
          >
            <X className="w-4 h-4" />
          </button>

          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#FF2D87] text-white text-[10px] font-bold tracking-[0.2em]">
            SAVE 15%
          </span>

          <h3 id="combo-popup-title" className="mt-4 font-display font-black text-3xl sm:text-4xl tracking-tighter">
            Starlights <span className="text-zinc-500">+</span> Ambient,
            <br />
            <span className="neon-violet">15% off</span> the lot.
          </h3>

          <p className="mt-4 text-zinc-300 text-sm font-body leading-relaxed">
            Booking both together? Pick any Starlight package and any Ambient package on
            the same booking and we'll knock <b className="text-white">15%</b> off your combined total.
            One install. One day. Same OEM finish.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to="/booking"
              onClick={close}
              data-testid="combo-popup-cta"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF2D87] text-white font-semibold text-sm hover:bg-[#ff5fa0] transition"
            >
              Build my combo
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </Link>
            <button
              type="button"
              onClick={close}
              data-testid="combo-popup-dismiss"
              className="text-sm text-zinc-400 hover:text-white px-3 py-2 transition"
            >
              Maybe later
            </button>
          </div>

          <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D946EF] to-transparent opacity-70" />
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px) scale(.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>
    </>
  );
}
